import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { CalendarCheck, UsersThree, Trophy, Drone, ShoppingBag, Buildings, QrCode, Target, HandHeart, Broadcast } from "@phosphor-icons/react";

/**
 * DashboardShowcase — colorful quick-action grid + stats + upcoming matches +
 * scan CTA, matching the mobile (2 col × 3 rows) & desktop (5 col row) layout.
 * Keeps the existing routes.
 */
export default function DashboardShowcase() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ matches: 0, friends: 0, next_date: null });
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    // Pull a light stats bundle. We fail soft so the UI never breaks.
    api.get("/me/stats-overview").then(({ data }) => {
      setStats({
        matches: data?.matches ?? 0,
        friends: data?.friends ?? 0,
        next_date: data?.next_date ?? null,
      });
      setUpcoming(data?.upcoming || []);
    }).catch(() => {});
  }, []);

  const nivel = user?.category || "Intermedio";
  const estilo = user?.playing_style || "Ofensivo";
  const rendimiento = user?.performance_pct ?? 82;

  // The 5 action cards reused in both layouts. For mobile we show 6 (adds Clubs).
  const CARDS = [
    { id: "partner",   to: "/dashboard/partner-match", label: "Partner Match", sub: "Encuentra tu dupla", icon: HandHeart,  color: "from-pink-500/80 to-rose-700/60",      border: "border-pink-400/50",   testId: "qa-partner-match", badge: "NEW" },
    { id: "retas",     to: "/dashboard/retas",      label: "Buscar retas",     sub: "Juega con tu nivel", icon: Target,      color: "from-emerald-500/80 to-emerald-700/60", border: "border-emerald-400/40", testId: "qa-retas" },
    { id: "torneos",   to: "/dashboard/torneos",    label: "Torneos",          sub: "Compite y gana",     icon: Trophy,      color: "from-sky-500/80 to-blue-700/60",       border: "border-sky-400/40",     testId: "qa-torneos" },
    { id: "live",      to: "/dashboard/comunidad-live", label: "Comunidad Live", sub: "Mira y transmite",  icon: Broadcast,  color: "from-red-500/80 to-rose-700/60",       border: "border-red-400/50",     testId: "qa-live", badge: "LIVE" },
    { id: "comunidad", to: "/dashboard/comunidad",  label: "Mi comunidad",     sub: "Conecta y comparte", icon: UsersThree,  color: "from-purple-500/80 to-fuchsia-700/60", border: "border-purple-400/40",  testId: "qa-comunidad" },
    { id: "canchas",   to: "/dashboard/canchas",    label: "Reservar cancha",  sub: "Pistas disponibles", icon: CalendarCheck,color: "from-blue-500/80 to-indigo-700/60",   border: "border-blue-400/40",    testId: "qa-canchas" },
    { id: "tienda",    to: "/dashboard/tienda",     label: "Tienda DYD",       sub: "Ropa y accesorios",  icon: ShoppingBag, color: "from-teal-500/80 to-cyan-700/60",      border: "border-teal-400/40",    testId: "qa-tienda" },
  ];
  const MOBILE_EXTRA = [
    { id: "aerotomas", to: "/dashboard",            label: "DYD + Aerotomas",  sub: "Vigilancia aérea y video", icon: Drone, color: "from-orange-500/80 to-red-700/60", border: "border-orange-400/40", testId: "qa-aerotomas" },
    { id: "clubes",    to: "/dashboard/clubes",     label: "Clubs",            sub: "Encuentra tu club",  icon: Buildings,   color: "from-cyan-500/80 to-teal-700/60",       border: "border-cyan-400/40",    testId: "qa-clubes" },
  ];

  const Card = ({ to, label, sub, icon: Icon, color, border, testId, badge }) => (
    <Link
      to={to}
      className={`group relative overflow-hidden rounded-xl border ${border} bg-gradient-to-br ${color} p-4 md:p-5 min-h-[108px] md:min-h-[150px] flex flex-col justify-between transition-all duration-300 hover:scale-[1.03] hover:-translate-y-0.5 hover:shadow-[0_12px_40px_rgba(0,0,0,0.5),0_0_0_1px_rgba(204,255,0,0.3)]`}
      data-testid={testId}
    >
      {/* Decorative orbital glow */}
      <div aria-hidden className="pointer-events-none absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "radial-gradient(circle, rgba(204,255,0,0.3) 0%, transparent 70%)" }} />
      {badge && (
        <span className={`absolute top-2 right-2 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest border ${badge === "LIVE" ? "bg-rose-500 text-white border-white/40 animate-pulse" : "bg-[#CCFF00] text-black border-black/20"} shadow`}>
          {badge}
        </span>
      )}
      <Icon size={30} weight="duotone" className="text-white drop-shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300" />
      <div className="relative">
        <h3 className="font-['Barlow_Condensed'] text-xl md:text-2xl font-black uppercase text-white leading-tight tracking-tight drop-shadow">{label}</h3>
        <p className="text-[11px] md:text-xs text-white/90 mt-0.5">{sub}</p>
      </div>
      <span className="absolute bottom-2 right-2 w-7 h-7 md:w-8 md:h-8 rounded-full bg-[#CCFF00] text-black flex items-center justify-center text-xs font-black group-hover:translate-x-1 group-hover:scale-110 transition-all">›</span>
    </Link>
  );

  return (
    <div className="space-y-4" data-testid="dashboard-showcase">
      {/* Floating player stats row (visible on all sizes) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5">
        <div className="card-tactical p-3 border-[#CCFF00]/20" data-testid="stats-nivel">
          <p className="text-[9px] uppercase tracking-widest text-[#CCFF00]">Nivel</p>
          <p className="font-['Barlow_Condensed'] text-2xl font-black uppercase text-white truncate">{nivel}</p>
        </div>
        <div className="card-tactical p-3 border-[#CCFF00]/20" data-testid="stats-estilo">
          <p className="text-[9px] uppercase tracking-widest text-[#CCFF00]">Estilo</p>
          <p className="font-['Barlow_Condensed'] text-2xl font-black uppercase text-white truncate">{estilo}</p>
        </div>
        <div className="card-tactical p-3 border-[#CCFF00]/20" data-testid="stats-rendimiento">
          <p className="text-[9px] uppercase tracking-widest text-[#CCFF00]">Rendimiento</p>
          <p className="font-['Barlow_Condensed'] text-2xl font-black text-white"><span className="text-[#CCFF00]">{rendimiento}</span>%</p>
        </div>
        <div className="card-tactical p-3 border-[#CCFF00]/20 col-span-2 md:col-span-1" data-testid="stats-partidos">
          <p className="text-[9px] uppercase tracking-widest text-[#CCFF00]">Partidos · Amigos</p>
          <p className="font-['Barlow_Condensed'] text-2xl font-black text-white">
            <span className="text-[#CCFF00]">{stats.matches}</span> · <span className="text-[#CCFF00]">{stats.friends}</span>
          </p>
        </div>
        <Link to="/dashboard/retas" className="card-tactical p-3 border-[#CCFF00]/50 bg-[#CCFF00]/5 hover:bg-[#CCFF00]/10 transition-colors col-span-2 md:col-span-1 flex flex-col justify-center items-center text-center group" data-testid="stats-cta-reta">
          <p className="text-[9px] uppercase tracking-widest text-[#CCFF00] font-bold">Tu próxima reta</p>
          <p className="font-['Barlow_Condensed'] text-xl font-black text-white group-hover:text-[#CCFF00] transition-colors">te espera →</p>
        </Link>
      </div>

      {/* Upcoming encounters — admin-populated or inferred (chat below hero) */}
      {upcoming.length > 0 && (
        <div className="card-tactical p-4" data-testid="dashboard-upcoming">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#CCFF00]">Tus próximos encuentros</p>
            <Link to="/dashboard/retas" className="text-[10px] uppercase tracking-widest text-gray-400 hover:text-[#CCFF00]">Ver todos ›</Link>
          </div>
          <div className="divide-y divide-white/5">
            {upcoming.slice(0, 2).map((u, i) => (
              <div key={i} className="flex items-center gap-3 py-2.5" data-testid={`upcoming-row-${i}`}>
                <div className="shrink-0 w-14 bg-[#CCFF00]/10 border border-[#CCFF00]/30 rounded px-1 py-1.5 text-center">
                  <p className="text-[10px] uppercase font-bold text-[#CCFF00] leading-none">{u.day_short || "—"}</p>
                  <p className="font-['Barlow_Condensed'] text-2xl font-black text-white leading-none mt-0.5">{u.day_num || "—"}</p>
                  <p className="text-[9px] uppercase text-gray-400 leading-none mt-0.5">{u.month_short || ""}</p>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-['Barlow_Condensed'] text-lg font-black uppercase text-white truncate">{u.title}</p>
                  <p className="text-xs text-gray-400">{u.time_range || ""} · {u.location || ""}</p>
                </div>
                <span className={`px-2 py-1 rounded text-[9px] uppercase font-bold border ${u.state === "confirmed" ? "bg-[#CCFF00]/15 text-[#CCFF00] border-[#CCFF00]/30" : "bg-blue-500/15 text-blue-300 border-blue-400/30"}`}>
                  {u.state === "confirmed" ? "Confirmada" : "Inscripción abierta"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick action cards — desktop 5 col, mobile 2 col × 3 rows */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5 md:gap-3" data-testid="dashboard-quick-actions">
        {CARDS.map((c) => (<Card key={c.id} {...c} />))}
        {MOBILE_EXTRA.map((c) => (
          <div key={c.id} className="md:hidden"><Card {...c} /></div>
        ))}
      </div>

      {/* Scan CTA (mobile emphasised, same card on desktop) */}
      <Link to="/dashboard/canchas" className="relative overflow-hidden rounded-xl border border-[#CCFF00]/40 bg-gradient-to-r from-black via-[#0a0a0a] to-[#1a2a0a] p-4 flex items-center gap-3 hover:border-[#CCFF00] transition-all" data-testid="dashboard-scan-cta">
        <QrCode size={38} weight="duotone" className="text-[#CCFF00] shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-['Barlow_Condensed'] text-xl font-black uppercase text-white leading-tight tracking-tight">Escanea y juega</p>
          <p className="text-xs text-gray-400">Tu acceso rápido a la cancha</p>
        </div>
        <span className="shrink-0 w-10 h-10 rounded-full bg-[#CCFF00] text-black flex items-center justify-center font-black text-lg">›</span>
      </Link>
    </div>
  );
}
